import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { UploadSimple, FilePdf, VideoCamera, File as FileIcon, Download, Trash } from "@phosphor-icons/react";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

function iconFor(kind) {
  if (kind === "pdf") return <FilePdf size={22} weight="duotone" color="#E02424" />;
  if (kind === "video") return <VideoCamera size={22} weight="duotone" color="#002FA7" />;
  return <FileIcon size={22} weight="duotone" />;
}

export default function MaterialsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [students, setStudents] = useState([]);
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", selected: [] });
  const [uploading, setUploading] = useState(false);

  async function load() {
    const r = await api.get("/materials"); setItems(r.data);
    if (user?.role === "admin" || user?.role === "teacher") {
      const s = await api.get("/students"); setStudents(s.data);
    }
  }
  useEffect(() => { load(); }, []);

  function toggleStudent(id) {
    setForm(f => ({
      ...f,
      selected: f.selected.includes(id) ? f.selected.filter(x => x !== id) : [...f.selected, id],
    }));
  }

  async function upload() {
    if (!file || !form.title) { toast.error("Título e arquivo obrigatórios"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", form.title);
      fd.append("description", form.description);
      fd.append("student_ids", form.selected.join(","));
      await api.post("/materials/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Material enviado");
      setOpen(false); setFile(null); setForm({ title: "", description: "", selected: [] });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha no upload");
    } finally { setUploading(false); }
  }

  async function download(m) {
    try {
      const resp = await api.get(`/materials/${m.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = m.original_filename || m.title;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (e) { toast.error("Erro ao baixar"); }
  }

  async function preview(m) {
    // open in new tab using blob URL
    try {
      const resp = await api.get(`/materials/${m.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      window.open(url, "_blank");
    } catch (e) { toast.error("Erro ao abrir"); }
  }

  async function remove(id) {
    if (!confirm("Excluir material?")) return;
    await api.delete(`/materials/${id}`);
    toast.success("Excluído"); load();
  }

  return (
    <div className="p-4 sm:p-8 lg:p-10 max-w-6xl mx-auto space-y-6">
      <header className="flex items-end justify-between fade-up flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#1E5FFF]">Materiais</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter mt-2">
            {(user?.role === "admin" || user?.role === "teacher") ? "Biblioteca de materiais" : "Meus materiais"}
          </h1>
          <p className="text-zinc-500 text-sm mt-2">
            {user?.role === "admin"
              ? "Envie PDFs e vídeos e defina para quais alunos ficam visíveis."
              : user?.role === "teacher"
              ? "Envie PDFs e vídeos para seus alunos. Deixe a seleção em branco para liberar a todos os SEUS alunos."
              : "Materiais liberados para você pelo administrador ou professor."}
          </p>
        </div>
        {(user?.role === "admin" || user?.role === "teacher") && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="material-new" className="bg-[#002FA7] hover:bg-[#001f7a] brutal-shadow">
                <UploadSimple size={16} className="mr-2" weight="bold" /> Novo material
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>Upload de material</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>Arquivo (PDF ou vídeo, até 200MB)</Label>
                  <Input data-testid="m-file" type="file" accept=".pdf,video/*,application/pdf"
                         onChange={(e) => setFile(e.target.files?.[0] || null)} className="mt-2" />
                  {file && <div className="text-xs text-zinc-500 mt-1 font-mono-num">{file.name} • {(file.size/1024/1024).toFixed(1)} MB</div>}
                </div>
                <div><Label>Título</Label><Input data-testid="m-title" value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="mt-2" /></div>
                <div><Label>Descrição</Label><Textarea data-testid="m-desc" value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="mt-2" rows={2} /></div>
                <div>
                  <Label>Visibilidade</Label>
                  <div className="text-xs text-zinc-500 mt-1 mb-2">
                    {form.selected.length === 0 ? "Todos os alunos verão este material." : `Apenas ${form.selected.length} aluno(s) selecionado(s).`}
                  </div>
                  <div className="max-h-40 overflow-y-auto border border-zinc-200 rounded p-2 space-y-1">
                    {students.map(s => (
                      <label key={s.id} className="flex items-center gap-2 text-sm py-1 hover:bg-zinc-50 rounded px-1 cursor-pointer">
                        <Checkbox data-testid={`m-student-${s.id}`} checked={form.selected.includes(s.id)} onCheckedChange={() => toggleStudent(s.id)} />
                        <span className="flex-1">{s.name}</span>
                        <span className="text-xs text-zinc-400">{s.email}</span>
                      </label>
                    ))}
                    {students.length === 0 && <div className="text-xs text-zinc-400 py-4 text-center">Nenhum aluno cadastrado.</div>}
                  </div>
                </div>
                <Button data-testid="m-submit" onClick={upload} disabled={uploading} className="w-full bg-[#002FA7] hover:bg-[#001f7a]">
                  {uploading ? "Enviando..." : "Enviar material"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </header>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 fade-up">
        {items.length === 0 && (
          <div className="col-span-full text-center py-16 text-zinc-400 border border-dashed border-zinc-300 rounded-md dot-grid">
            Nenhum material disponível ainda.
          </div>
        )}
        {items.map(m => (
          <div key={m.id} data-testid={`material-${m.id}`} className="bg-white border border-zinc-200 rounded-md p-5 flex flex-col hover:border-zinc-400 transition-colors">
            <div className="flex items-start gap-3">
              {iconFor(m.kind)}
              <div className="flex-1 min-w-0">
                <div className="font-heading font-bold text-base truncate">{m.title}</div>
                <div className="text-xs text-zinc-500 truncate">{m.original_filename}</div>
              </div>
            </div>
            {m.description && <p className="text-sm text-zinc-600 mt-3 line-clamp-3">{m.description}</p>}
            <div className="mt-3 flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold text-zinc-500">
              <span className="font-mono-num">{(m.size/1024/1024).toFixed(1)} MB</span>
              <span>•</span>
              <span>{m.kind}</span>
              {m.student_ids?.length > 0 && <><span>•</span><span>{m.student_ids.length} aluno(s)</span></>}
              {(!m.student_ids || m.student_ids.length === 0) && <><span>•</span><span className="text-[#002FA7]">Todos</span></>}
            </div>
            <div className="mt-4 flex gap-2">
              <Button data-testid={`preview-${m.id}`} variant="outline" size="sm" onClick={() => preview(m)} className="flex-1">
                Abrir
              </Button>
              <Button data-testid={`dl-${m.id}`} size="sm" onClick={() => download(m)} className="flex-1 bg-[#002FA7] hover:bg-[#001f7a]">
                <Download size={14} className="mr-1" /> Baixar
              </Button>
              {(user?.role === "admin" || user?.role === "teacher") && (m.uploaded_by === user.id || user.role === "admin") && (
                <Button variant="ghost" size="sm" onClick={() => remove(m.id)}><Trash size={14} /></Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
